/**
 * SessionManager — Manages multiple concurrent SIP sessions.
 *
 * Maintains an indexed collection of ManagedSession objects, enforces
 * the 4-session cap, designates exactly one active session, and emits
 * granular lifecycle events.
 */

// ---------- Interfaces ----------

export interface ManagedSession {
  id: string;
  session: any; // Raw JsSIP.RTCSession object
  status: "ringing" | "active" | "held" | "merging" | "ended";
  direction: "inbound" | "outbound";
  remoteIdentity: string;
  displayName: string;
  startTime: number;
  holdStartTime: number | null;
  isMuted: boolean;
  remoteStream: MediaStream | null;
  addedAt: number;
}

export interface SessionManagerEvents {
  sessionAdded: (session: ManagedSession) => void;
  sessionRemoved: (sessionId: string) => void;
  sessionUpdated: (session: ManagedSession) => void;
  activeSessionChanged: (sessionId: string | null) => void;
  maxSessionsReached: () => void;
  swapStarted: (fromId: string, toId: string) => void;
  swapCompleted: (fromId: string, toId: string) => void;
  swapFailed: (error: string) => void;
  swapCriticalFailure: (error: string) => void;
  holdFailed: (sessionId: string, error: string) => void;
  resumeFailed: (sessionId: string, error: string) => void;
}

type SessionManagerEventName = keyof SessionManagerEvents;
type EventHandler = (...args: any[]) => void;

// ---------- Class ----------

export class SessionManager {
  private sessions: Map<string, ManagedSession> = new Map();
  private activeSessionId: string | null = null;
  private listeners: Map<string, Set<EventHandler>> = new Map();

  public readonly MAX_SESSIONS = 4;

  // ---------- Event emitter ----------

  on<K extends SessionManagerEventName>(event: K, handler: SessionManagerEvents[K]): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as EventHandler);
  }

  off<K extends SessionManagerEventName>(event: K, handler: SessionManagerEvents[K]): void {
    this.listeners.get(event)?.delete(handler as EventHandler);
  }

  private emit<K extends SessionManagerEventName>(event: K, ...args: Parameters<SessionManagerEvents[K]>): void {
    this.listeners.get(event)?.forEach((handler) => {
      try {
        handler(...args);
      } catch (e) {
        console.error(`[SessionManager] Error in ${event} handler:`, e);
      }
    });
  }

  // ---------- Core operations ----------

  /**
   * Add a new session to the manager.
   * Returns the ManagedSession if added successfully, or null if at capacity.
   */
  addSession(
    session: any,
    direction: "inbound" | "outbound",
    options?: {
      remoteIdentity?: string;
      displayName?: string;
      id?: string;
    }
  ): ManagedSession | null {
    if (!this.canAddSession()) {
      this.emit("maxSessionsReached");
      return null;
    }

    const id = options?.id ?? this.generateSessionId(session);
    const now = Date.now();

    const managed: ManagedSession = {
      id,
      session,
      status: "ringing",
      direction,
      remoteIdentity: options?.remoteIdentity ?? "",
      displayName: options?.displayName ?? "",
      startTime: now,
      holdStartTime: null,
      isMuted: false,
      remoteStream: null,
      addedAt: now,
    };

    this.sessions.set(id, managed);

    // If this is the first session, designate it as active
    if (this.sessions.size === 1) {
      this.activeSessionId = id;
      this.emit("activeSessionChanged", id);
    }

    this.emit("sessionAdded", managed);
    return managed;
  }

  /**
   * Remove a session from the manager.
   * When removed, auto-designates the most recently added remaining session as active.
   */
  removeSession(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    this.sessions.delete(sessionId);
    this.emit("sessionRemoved", sessionId);

    // If the removed session was the active one, pick a new active
    if (this.activeSessionId === sessionId) {
      if (this.sessions.size > 0) {
        // Auto-designate the most recently added remaining session
        const mostRecent = this.getMostRecentSession();
        this.activeSessionId = mostRecent ? mostRecent.id : null;
      } else {
        this.activeSessionId = null;
      }
      this.emit("activeSessionChanged", this.activeSessionId);
    }
  }

  /**
   * Get a session by its ID.
   */
  getSession(sessionId: string): ManagedSession | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * Get all sessions as an array.
   */
  getAllSessions(): ManagedSession[] {
    return Array.from(this.sessions.values());
  }

  /**
   * Get the currently active session.
   */
  getActiveSession(): ManagedSession | null {
    if (!this.activeSessionId) return null;
    return this.sessions.get(this.activeSessionId) ?? null;
  }

  /**
   * Set the active session by ID.
   */
  setActiveSession(sessionId: string): void {
    if (!this.sessions.has(sessionId)) return;
    if (this.activeSessionId === sessionId) return;

    this.activeSessionId = sessionId;
    this.emit("activeSessionChanged", sessionId);
  }

  /**
   * Get the current number of tracked sessions.
   */
  getSessionCount(): number {
    return this.sessions.size;
  }

  /**
   * Returns true if a new session can be added (count < MAX_SESSIONS).
   */
  canAddSession(): boolean {
    return this.sessions.size < this.MAX_SESSIONS;
  }

  /**
   * Get the active session ID (exposed for read access).
   */
  getActiveSessionId(): string | null {
    return this.activeSessionId;
  }

  // ---------- Session update helper ----------

  /**
   * Update fields on a managed session and emit the sessionUpdated event.
   */
  updateSession(sessionId: string, changes: Partial<Omit<ManagedSession, "id" | "session">>): ManagedSession | undefined {
    const session = this.sessions.get(sessionId);
    if (!session) return undefined;

    Object.assign(session, changes);
    this.emit("sessionUpdated", session);
    return session;
  }

  // ---------- Hold / Resume / Swap ----------

  /**
   * Place a session on SIP hold via re-INVITE.
   * Wraps JsSIP session.hold() in a Promise with a 10-second timeout.
   * No-op if the session is already held.
   * On failure or timeout, emits "holdFailed" and leaves state unchanged.
   */
  async holdSession(sessionId: string): Promise<void> {
    const managed = this.sessions.get(sessionId);
    if (!managed) return;

    // No-op if already held (Req 3.9)
    if (managed.status === "held") return;

    const HOLD_TIMEOUT_MS = 10000;

    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.emit("holdFailed", sessionId, "Hold operation timed out after 10 seconds");
        reject(new Error("Hold operation timed out"));
      }, HOLD_TIMEOUT_MS);

      try {
        const rawSession = managed.session;

        rawSession.hold({
          eventHandlers: {
            succeeded: () => {
              clearTimeout(timer);
              managed.status = "held";
              managed.holdStartTime = Date.now();
              this.emit("sessionUpdated", managed);
              resolve();
            },
            failed: (event: any) => {
              clearTimeout(timer);
              const errorMsg = event?.cause || "Hold re-INVITE failed";
              this.emit("holdFailed", sessionId, errorMsg);
              reject(new Error(errorMsg));
            },
          },
        });
      } catch (error: any) {
        clearTimeout(timer);
        const errorMsg = error?.message || "Hold operation threw an exception";
        this.emit("holdFailed", sessionId, errorMsg);
        reject(new Error(errorMsg));
      }
    });
  }

  /**
   * Resume a held session via SIP re-INVITE.
   * Wraps JsSIP session.unhold() in a Promise with a 10-second timeout.
   * On failure or timeout, emits "resumeFailed" and leaves state unchanged.
   */
  async unholdSession(sessionId: string): Promise<void> {
    const managed = this.sessions.get(sessionId);
    if (!managed) return;

    const UNHOLD_TIMEOUT_MS = 10000;

    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.emit("resumeFailed", sessionId, "Resume operation timed out after 10 seconds");
        reject(new Error("Resume operation timed out"));
      }, UNHOLD_TIMEOUT_MS);

      try {
        const rawSession = managed.session;

        rawSession.unhold({
          eventHandlers: {
            succeeded: () => {
              clearTimeout(timer);
              managed.status = "active";
              managed.holdStartTime = null;
              this.emit("sessionUpdated", managed);
              resolve();
            },
            failed: (event: any) => {
              clearTimeout(timer);
              const errorMsg = event?.cause || "Resume re-INVITE failed";
              this.emit("resumeFailed", sessionId, errorMsg);
              reject(new Error(errorMsg));
            },
          },
        });
      } catch (error: any) {
        clearTimeout(timer);
        const errorMsg = error?.message || "Resume operation threw an exception";
        this.emit("resumeFailed", sessionId, errorMsg);
        reject(new Error(errorMsg));
      }
    });
  }

  /**
   * Swap the active call with the target held call.
   *
   * Flow:
   * 1. Emit "swapStarted"
   * 2. Hold the current active session
   * 3. Resume the target session (retry up to 2 times on failure, 1s delay)
   * 4. If all resume retries fail, recover by resuming the original active session
   * 5. If recovery also fails, emit "swapCriticalFailure"
   * 6. On success, emit "swapCompleted"
   * 7. On failure but recovery succeeded, emit "swapFailed"
   */
  async swapTo(targetSessionId: string): Promise<void> {
    const currentActive = this.getActiveSession();
    if (!currentActive) return;

    const target = this.sessions.get(targetSessionId);
    if (!target) return;

    // No-op if target is already the active session
    if (currentActive.id === targetSessionId) return;

    const originalActiveId = currentActive.id;

    // Step 1: Emit swapStarted
    this.emit("swapStarted", originalActiveId, targetSessionId);

    // Step 2: Hold the current active session
    try {
      await this.holdSession(originalActiveId);
    } catch {
      // Hold failed — swap cannot proceed
      this.emit("swapFailed", `Failed to hold session ${originalActiveId}`);
      return;
    }

    // Step 3: Resume target with retry (up to 2 retries = 3 total attempts)
    const MAX_RESUME_RETRIES = 2;
    let resumeSucceeded = false;

    for (let attempt = 0; attempt <= MAX_RESUME_RETRIES; attempt++) {
      try {
        await this.unholdSession(targetSessionId);
        resumeSucceeded = true;
        break;
      } catch {
        // Wait 1 second before retrying (except after last attempt)
        if (attempt < MAX_RESUME_RETRIES) {
          await this.delay(1000);
        }
      }
    }

    if (resumeSucceeded) {
      // Update active session designation
      this.activeSessionId = targetSessionId;
      this.emit("activeSessionChanged", targetSessionId);
      this.emit("swapCompleted", originalActiveId, targetSessionId);
      return;
    }

    // Step 4: All resume retries failed — attempt to recover original
    try {
      await this.unholdSession(originalActiveId);
      // Recovery succeeded — original is active again
      this.activeSessionId = originalActiveId;
      this.emit("activeSessionChanged", originalActiveId);
      this.emit("swapFailed", `Failed to resume target session ${targetSessionId}, recovered original`);
    } catch {
      // Step 5: Recovery also failed — critical failure
      this.emit("swapCriticalFailure", `Swap failed: could not resume target ${targetSessionId} or recover original ${originalActiveId}. Manual reconnection required.`);
    }
  }

  // ---------- Private helpers ----------

  /**
   * Find the most recently added session (highest addedAt value).
   */
  private getMostRecentSession(): ManagedSession | null {
    let mostRecent: ManagedSession | null = null;

    this.sessions.forEach((session) => {
      if (!mostRecent || session.addedAt > mostRecent.addedAt) {
        mostRecent = session;
      }
    });

    return mostRecent;
  }

  /**
   * Utility delay for retry logic.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Generate a unique session ID from the JsSIP session or a fallback UUID.
   */
  private generateSessionId(session: any): string {
    // Prefer the JsSIP session's internal ID if available
    if (session?.id) return session.id;
    // Fallback: generate a simple unique string
    return `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
}
